import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Sparkles, Camera, Upload, User } from 'lucide-react';
import { toast } from 'sonner';
import { PlanBadge } from '@/components/PlanBadge';
import { PlanStatusCard } from '@/components/PlanStatusCard';
import { LocationPreferences } from '@/components/LocationPreferences';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    bio: '',
    phone: '',
    picture: '',
    date_of_birth: '',
    gender: '',
    location: { city: '', country: '' },
    relationship_goals: '',
    core_values: [],
    faith_spiritual: '',
    love_languages: [],
    lifestyle_habits: { exercise: '', diet: '', smoking: '', drinking: '' },
    personality_type: '',
    commitment_readiness: 5,
    emotional_availability: 5,
    preferred_locations: [],
    open_to_long_distance: false,
    willing_to_relocate: '',
    relocation_who: ''
  });

  useEffect(() => {
    fetchCompleteProfile();
    fetchSubscription();
  }, []);

  const fetchCompleteProfile = async () => {
    setProfileLoading(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/profile/${user.id}`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      const profileData = response.data;
      
      // Set form data with all profile information
      setFormData({
        name: profileData.name || '',
        email: profileData.email || '',
        bio: profileData.bio || '',
        phone: profileData.phone || '',
        picture: profileData.picture || '',
        date_of_birth: profileData.date_of_birth || '',
        gender: profileData.gender || '',
        location: profileData.location || { city: '', country: '' },
        relationship_goals: profileData.relationship_goals || '',
        core_values: profileData.core_values || [],
        faith_spiritual: profileData.faith_spiritual || '',
        love_languages: profileData.love_languages || [],
        lifestyle_habits: profileData.lifestyle_habits || { exercise: '', diet: '', smoking: '', drinking: '' },
        personality_type: profileData.personality_type || '',
        commitment_readiness: profileData.commitment_readiness || 5,
        emotional_availability: profileData.emotional_availability || 5,
        preferred_locations: profileData.preferred_locations || [],
        open_to_long_distance: profileData.open_to_long_distance || false,
        willing_to_relocate: profileData.willing_to_relocate || '',
        relocation_who: profileData.relocation_who || ''
      });
      
      setPhotoPreview(profileData.picture || null);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to load complete profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/payments/subscription`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const cancelSubscription = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/payments/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      setSubscription(response.data);
      toast.success('Your subscription has been cancelled. You are now on the Free plan.');
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      toast.error(error.response?.data?.detail || 'Could not cancel subscription');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image too large. Maximum size is 5MB.');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setPhotoPreview(base64String);
        setFormData({ ...formData, picture: base64String });
        toast.success('Photo selected! Click Save Changes to update.');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setPhotoPreview(base64String);
        setFormData({ ...formData, picture: base64String });
        toast.success('Photo captured! Click Save Changes to update.');
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setFormData({ ...formData, picture: '' });
    toast.success('Photo removed! Click Save Changes to update.');
  };

  const generateBio = async () => {
    setAiLoading(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.post(
        `${API}/ai/generate-bio`,
        {
          prompt: `Name: ${formData.name}, Interests: ${user.core_values?.join(', ')}, Goals: ${user.relationship_goals}`
        },
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      setFormData({ ...formData, bio: response.data.bio });
      toast.success('Bio generated!');
    } catch (error) {
      console.error('Failed to generate bio:', error);
      toast.error('Failed to generate bio');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.put(`${API}/profile`, formData, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      toast.success('Profile updated!');
      
      // Refresh user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      setUser(userResponse.data);
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-white to-amber-50">
        <div className="animate-pulse text-2xl font-light text-rose-600">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button
          data-testid="back-to-dashboard-btn"
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-rose-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="flex items-center justify-between mb-8">
          <h1 className="text-5xl font-bold text-rose-900" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Your Profile
          </h1>
          {subscription && <PlanBadge planType={subscription.plan_type} className="px-6 py-3 text-base shadow-lg" />}
        </div>

        {subscription && (
          <div className="mb-8">
            <PlanStatusCard subscription={subscription} onCancel={cancelSubscription} />
          </div>
        )}

        <Card className="shadow-xl border-rose-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Update your details and preferences</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Profile Photo Section */}
              <div className="flex flex-col items-center gap-4 p-6 bg-gradient-to-br from-rose-50 to-amber-50 rounded-lg border border-rose-100">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-rose-200 to-amber-200 flex items-center justify-center border-4 border-white shadow-lg">
                    {photoPreview ? (
                      <img 
                        src={photoPreview} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                        data-testid="profile-photo-preview"
                      />
                    ) : (
                      <User className="w-16 h-16 text-rose-400" />
                    )}
                  </div>
                </div>
                
                <div className="text-center">
                  <p className="text-sm font-medium text-rose-900 mb-2">Profile Photo</p>
                  <p className="text-xs text-gray-600 mb-4">Upload a photo or use your camera (max 5MB)</p>
                </div>

                <div className="flex flex-wrap gap-3 justify-center">
                  <label htmlFor="photo-upload">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-rose-200 cursor-pointer"
                      onClick={() => document.getElementById('photo-upload').click()}
                      data-testid="upload-photo-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Photo
                    </Button>
                    <input
                      id="photo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                  </label>

                  <label htmlFor="camera-capture">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-rose-200 cursor-pointer"
                      onClick={() => document.getElementById('camera-capture').click()}
                      data-testid="camera-capture-btn"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Take Photo
                    </Button>
                    <input
                      id="camera-capture"
                      type="file"
                      accept="image/*"
                      capture="user"
                      onChange={handleCameraCapture}
                      className="hidden"
                    />
                  </label>

                  {photoPreview && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={removePhoto}
                      className="border-rose-200 text-rose-600"
                      data-testid="remove-photo-btn"
                    >
                      Remove Photo
                    </Button>
                  )}
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    data-testid="profile-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="profile-email-input"
                    type="email"
                    value={formData.email}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    data-testid="profile-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="dob">Date of Birth</Label>
                  <Input
                    id="dob"
                    data-testid="profile-dob-input"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="gender">Gender</Label>
                  <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                    <SelectTrigger data-testid="profile-gender-select">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="goals">Relationship Goals</Label>
                  <Select value={formData.relationship_goals} onValueChange={(v) => setFormData({ ...formData, relationship_goals: v })}>
                    <SelectTrigger data-testid="profile-goals-select">
                      <SelectValue placeholder="What are you looking for?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="marriage">Marriage</SelectItem>
                      <SelectItem value="long-term">Long-term Relationship</SelectItem>
                      <SelectItem value="life-partner">Life Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Location */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    data-testid="profile-city-input"
                    value={formData.location?.city || ''}
                    onChange={(e) => setFormData({ ...formData, location: { ...formData.location, city: e.target.value } })}
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    data-testid="profile-country-input"
                    value={formData.location?.country || ''}
                    onChange={(e) => setFormData({ ...formData, location: { ...formData.location, country: e.target.value } })}
                  />
                </div>
              </div>

              {/* Location preferences */}
              <LocationPreferences value={formData} onChange={(patch) => setFormData({ ...formData, ...patch })} />

              {/* Bio */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Button
                    data-testid="generate-bio-btn"
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={generateBio}
                    disabled={aiLoading}
                    className="border-rose-200 text-rose-600 hover:bg-rose-50"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {aiLoading ? 'Generating...' : 'AI Generate'}
                  </Button>
                </div>
                <Textarea
                  id="bio"
                  data-testid="profile-bio-textarea"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows={4}
                  placeholder="Tell us about yourself..."
                />
              </div>

              {/* Core Values */}
              <div>
                <Label>Core Values (Select all that apply)</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                  {['Honesty', 'Family', 'Faith', 'Ambition', 'Kindness', 'Loyalty', 'Adventure', 'Stability', 'Humor', 'Independence'].map((value) => (
                    <div key={value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`value-${value}`}
                        checked={formData.core_values?.includes(value)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({ ...formData, core_values: [...(formData.core_values || []), value] });
                          } else {
                            setFormData({ ...formData, core_values: formData.core_values?.filter(v => v !== value) || [] });
                          }
                        }}
                      />
                      <Label htmlFor={`value-${value}`} className="font-normal cursor-pointer">{value}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Love Languages */}
              <div>
                <Label>Love Languages (Select all that apply)</Label>
                <div className="space-y-3 mt-2">
                  {['Words of Affirmation', 'Quality Time', 'Physical Touch', 'Acts of Service', 'Receiving Gifts'].map((lang) => (
                    <div key={lang} className="flex items-center space-x-2">
                      <Checkbox
                        id={`lang-${lang}`}
                        checked={formData.love_languages?.includes(lang)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({ ...formData, love_languages: [...(formData.love_languages || []), lang] });
                          } else {
                            setFormData({ ...formData, love_languages: formData.love_languages?.filter(l => l !== lang) || [] });
                          }
                        }}
                      />
                      <Label htmlFor={`lang-${lang}`} className="font-normal cursor-pointer">{lang}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Faith */}
              <div>
                <Label htmlFor="faith">Faith/Spiritual Beliefs</Label>
                <Input
                  id="faith"
                  data-testid="profile-faith-input"
                  placeholder="e.g., Christian, Muslim, Spiritual, etc."
                  value={formData.faith_spiritual}
                  onChange={(e) => setFormData({ ...formData, faith_spiritual: e.target.value })}
                />
              </div>

              {/* Lifestyle Habits */}
              <div>
                <Label className="mb-3 block">Lifestyle Habits</Label>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="exercise" className="text-sm">Exercise</Label>
                    <Select
                      value={formData.lifestyle_habits?.exercise || ''}
                      onValueChange={(v) => setFormData({ ...formData, lifestyle_habits: { ...formData.lifestyle_habits, exercise: v } })}
                    >
                      <SelectTrigger data-testid="exercise-select">
                        <SelectValue placeholder="Frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="rarely">Rarely</SelectItem>
                        <SelectItem value="never">Never</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="diet" className="text-sm">Diet</Label>
                    <Select
                      value={formData.lifestyle_habits?.diet || ''}
                      onValueChange={(v) => setFormData({ ...formData, lifestyle_habits: { ...formData.lifestyle_habits, diet: v } })}
                    >
                      <SelectTrigger data-testid="diet-select">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vegan">Vegan</SelectItem>
                        <SelectItem value="vegetarian">Vegetarian</SelectItem>
                        <SelectItem value="pescatarian">Pescatarian</SelectItem>
                        <SelectItem value="omnivore">Omnivore</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="smoking" className="text-sm">Smoking</Label>
                    <Select
                      value={formData.lifestyle_habits?.smoking || ''}
                      onValueChange={(v) => setFormData({ ...formData, lifestyle_habits: { ...formData.lifestyle_habits, smoking: v } })}
                    >
                      <SelectTrigger data-testid="smoking-select">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="socially">Socially</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="drinking" className="text-sm">Drinking</Label>
                    <Select
                      value={formData.lifestyle_habits?.drinking || ''}
                      onValueChange={(v) => setFormData({ ...formData, lifestyle_habits: { ...formData.lifestyle_habits, drinking: v } })}
                    >
                      <SelectTrigger data-testid="drinking-select">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="socially">Socially</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Personality */}
              <div>
                <Label htmlFor="personality">Personality Type</Label>
                <Input
                  id="personality"
                  data-testid="personality-input"
                  placeholder="e.g., INTJ, Extroverted, etc."
                  value={formData.personality_type}
                  onChange={(e) => setFormData({ ...formData, personality_type: e.target.value })}
                />
              </div>

              {/* Commitment & Availability */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="commitment">Commitment Readiness (1-10)</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <Input
                      id="commitment"
                      data-testid="commitment-input"
                      type="range"
                      min="1"
                      max="10"
                      value={formData.commitment_readiness}
                      onChange={(e) => setFormData({ ...formData, commitment_readiness: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="text-2xl font-bold text-rose-600 w-12 text-center">{formData.commitment_readiness}</span>
                  </div>
                </div>
                <div>
                  <Label htmlFor="emotional">Emotional Availability (1-10)</Label>
                  <div className="flex items-center gap-4 mt-2">
                    <Input
                      id="emotional"
                      data-testid="emotional-input"
                      type="range"
                      min="1"
                      max="10"
                      value={formData.emotional_availability}
                      onChange={(e) => setFormData({ ...formData, emotional_availability: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                    <span className="text-2xl font-bold text-rose-600 w-12 text-center">{formData.emotional_availability}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  data-testid="save-profile-btn"
                  type="submit"
                  disabled={loading}
                  className="bg-rose-500 hover:bg-rose-600"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  data-testid="view-pricing-btn"
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/pricing')}
                  className="border-rose-200"
                >
                  View Premium
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfilePage;